<template>
  <div class="calendar-wrap">
    <!-- 통계 -->
    <div class="stats-row">
      <div class="stat"><span class="stat-label">근무일수</span><span class="stat-value">{{ workDays }}일</span></div>
      <div class="stat"><span class="stat-label">총 근무시간</span><span class="stat-value">{{ totalHoursText }}</span></div>
      <div class="stat"><span class="stat-label">서비스 횟수</span><span class="stat-value">{{ normalServiceCount }}회</span></div>
      <div class="stat" v-if="severeCareCount > 0"><span class="stat-label">중증수급자 가산</span><span class="stat-value severe-care">{{ severeCareCount }}회</span></div>
      <div class="stat"><span class="stat-label">담당 수급자</span><span class="stat-value">{{ uniquePatients.length }}명</span></div>
    </div>

    <!-- 달력 -->
    <div class="calendar">
      <div class="cal-header">
        <div class="weekday" v-for="day in weekDays" :key="day" :class="{ sun: day === '일', sat: day === '토' }">{{ day }}</div>
      </div>
      <div class="cal-body">
        <div 
          v-for="(day, idx) in calendarDays" 
          :key="idx"
          class="cal-day"
          :class="{ 
            'other': !day.isCurrentMonth,
            'today': day.isToday,
            'worked': getDayServices(day.date).length > 0,
            'holiday': isHoliday(day)
          }"
        >
          <div class="day-num" :class="{ sun: day.dayOfWeek === 0, sat: day.dayOfWeek === 6, hol: isHoliday(day) && day.dayOfWeek !== 0 }">
            {{ formatDayNumber(day) }}
            <span v-if="getHolidayName(day)" class="hol-name">{{ getHolidayName(day) }}</span>
          </div>
          <div v-if="day.isCurrentMonth" class="day-services">
            <div 
              v-for="(service, sIdx) in getDayServices(day.date).slice(0, 3)" 
              :key="sIdx"
              class="svc-item"
              :class="{ 'family': service.isFamily, 'severe-care': service.isSevereCare }"
            >
              <span class="svc-time">{{ service.isSevereCare ? '중증' : formatTime(service.startTime) }}</span>
              <span class="svc-name">{{ service.patientName }}</span>
            </div>
            <div v-if="getDayServices(day.date).length > 3" class="svc-more">+{{ getDayServices(day.date).length - 3 }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 수급자별 -->
    <div class="patient-stats">
      <div class="ps-title">수급자별 서비스</div>
      <div class="ps-list">
        <div v-for="p in patientStats" :key="p.name" class="ps-item">
          <span class="ps-name">{{ p.name }}</span>
          <span class="ps-count">{{ p.count }}회</span>
          <span class="ps-hours">{{ p.hours }}</span>
          <span v-if="p.severeCareCount > 0" class="ps-severe">중증{{ p.severeCareCount }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface Service {
  date: string;
  patientName: string;
  patientCode: string;
  sugaName: string;
  startTime: string;
  endTime: string;
  sugaMoney: number;
  isFamily: boolean;
  isSevereCare?: boolean;
}

interface Props {
  yearMonth: string;
  services: Service[];
  holidays: Record<string, string>;
}

const props = defineProps<Props>();

const weekDays = ['일', '월', '화', '수', '목', '금', '토'];

const year = computed(() => parseInt(props.yearMonth?.slice(0, 4) || new Date().getFullYear().toString()));
const month = computed(() => parseInt(props.yearMonth?.slice(4, 6) || (new Date().getMonth() + 1).toString()));

interface CalendarDay {
  day: number;
  date: string;
  isCurrentMonth: boolean;
  isToday: boolean;
  dayOfWeek: number;
  isFirstOfMonth: boolean;
  monthNum: number;
}

const calendarDays = computed<CalendarDay[]>(() => {
  const y = year.value;
  const m = month.value;
  
  const firstDay = new Date(y, m - 1, 1);
  const startDay = new Date(firstDay);
  startDay.setDate(startDay.getDate() - firstDay.getDay());
  
  const days: CalendarDay[] = [];
  const current = new Date(startDay);
  const today = new Date();
  const todayStr = formatDateStr(today);
  
  for (let i = 0; i < 42; i++) {
    const isCurrentMonth = current.getMonth() === m - 1;
    const dateStr = formatDateStr(current);
    const isFirstOfMonth = current.getDate() === 1;
    
    days.push({
      day: current.getDate(),
      date: dateStr,
      isCurrentMonth,
      isToday: dateStr === todayStr,
      dayOfWeek: current.getDay(),
      isFirstOfMonth,
      monthNum: current.getMonth() + 1
    });
    
    current.setDate(current.getDate() + 1);
  }
  
  return days;
});

const formatDayNumber = (day: CalendarDay): string => {
  if (day.isFirstOfMonth) return `${day.monthNum}/${day.day}`;
  return String(day.day);
};

const formatDateStr = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatTime = (time?: string): string => {
  if (!time) return '';
  const str = time.toString().replace(/[^0-9]/g, '');
  if (str.length >= 4) return `${str.slice(0, 2)}:${str.slice(2, 4)}`;
  return time;
};

const getDayServices = (dateStr: string): Service[] => {
  return props.services.filter(s => {
    const serviceDate = s.date?.includes('-') ? s.date : formatServiceDate(s.date);
    return serviceDate === dateStr;
  }).sort((a, b) => {
    const timeA = a.startTime?.replace(/[^0-9]/g, '') || '';
    const timeB = b.startTime?.replace(/[^0-9]/g, '') || '';
    return timeA.localeCompare(timeB);
  });
};

const formatServiceDate = (date: string): string => {
  if (!date) return '';
  const str = date.toString().replace(/[^0-9]/g, '');
  if (str.length === 8) return `${str.slice(0, 4)}-${str.slice(4, 6)}-${str.slice(6, 8)}`;
  return date;
};

const isHoliday = (day: CalendarDay): boolean => {
  if (day.dayOfWeek === 0) return true;
  return !!props.holidays[day.date];
};

const getHolidayName = (day: CalendarDay): string | null => {
  if (!day.isCurrentMonth) return null;
  return props.holidays[day.date] || null;
};

const normalServiceCount = computed(() => props.services.filter(s => !s.isSevereCare).length);
const severeCareCount = computed(() => props.services.filter(s => s.isSevereCare).length);

const workDays = computed(() => {
  const uniqueDates = new Set(props.services.filter(s => !s.isSevereCare).map(s => s.date?.includes('-') ? s.date : formatServiceDate(s.date)));
  return uniqueDates.size;
});

const totalHoursText = computed(() => {
  let totalMinutes = 0;
  props.services.filter(s => !s.isSevereCare).forEach(s => {
    const start = formatTime(s.startTime).split(':');
    const end = formatTime(s.endTime).split(':');
    if (start.length >= 2 && end.length >= 2) {
      const startMins = parseInt(start[0]) * 60 + parseInt(start[1]);
      const endMins = parseInt(end[0]) * 60 + parseInt(end[1]);
      totalMinutes += Math.max(0, endMins - startMins);
    }
  });
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return mins > 0 ? `${hours}시간 ${mins}분` : `${hours}시간`;
});

const uniquePatients = computed(() => {
  const patients = new Set(props.services.filter(s => !s.isSevereCare).map(s => s.patientName));
  return Array.from(patients).filter(Boolean);
});

const patientStats = computed(() => {
  const stats: Record<string, { count: number; minutes: number; severeCareCount: number }> = {};
  props.services.forEach(s => {
    if (!stats[s.patientName]) stats[s.patientName] = { count: 0, minutes: 0, severeCareCount: 0 };
    if (s.isSevereCare) {
      stats[s.patientName].severeCareCount++;
    } else {
      stats[s.patientName].count++;
      const start = formatTime(s.startTime).split(':');
      const end = formatTime(s.endTime).split(':');
      if (start.length >= 2 && end.length >= 2) {
        const startMins = parseInt(start[0]) * 60 + parseInt(start[1]);
        const endMins = parseInt(end[0]) * 60 + parseInt(end[1]);
        stats[s.patientName].minutes += Math.max(0, endMins - startMins);
      }
    }
  });
  return Object.entries(stats)
    .map(([name, data]) => {
      const hours = Math.floor(data.minutes / 60);
      const mins = data.minutes % 60;
      return { 
        name, 
        count: data.count, 
        hours: mins > 0 ? `${hours}시간 ${mins}분` : `${hours}시간`,
        severeCareCount: data.severeCareCount
      };
    })
    .sort((a, b) => b.count - a.count);
});
</script>

<style lang="scss" scoped>
$border: #d4d4d4;
$bg: #f5f5f5;
$text: #333;
$text-sub: #666;

.calendar-wrap {
  background: #fff;
  border: 1px solid $border;
}

.stats-row {
  display: flex;
  border-bottom: 1px solid $border;

  .stat {
    flex: 1;
    padding: 12px;
    text-align: center;
    border-right: 1px solid $border;
    &:last-child { border-right: none; }

    .stat-label { display: block; font-size: 11px; color: $text-sub; margin-bottom: 2px; }
    .stat-value { display: block; font-size: 14px; font-weight: 600; color: $text; }
  }
}

.calendar {
  .cal-header {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    background: #fafafa;
    border-bottom: 1px solid $border;

    .weekday {
      padding: 8px;
      text-align: center;
      font-size: 12px;
      font-weight: 500;
      color: $text-sub;
      &.sun { color: #dc2626; }
      &.sat { color: #2563eb; }
    }
  }

  .cal-body {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
  }

  .cal-day {
    min-height: 72px;
    padding: 4px;
    border-right: 1px solid #eee;
    border-bottom: 1px solid #eee;
    background: #fff;

    &:nth-child(7n) { border-right: none; }
    &.other { background: #fafafa; .day-num { color: #ccc; } }
    &.today { background: #fffbeb; }
    &.worked { background: #f0fdf4; }
    &.holiday:not(.other) { background: #fef2f2; }
    &.holiday.worked:not(.other) { background: #fef2f2; }

    .day-num {
      font-size: 12px;
      font-weight: 500;
      color: $text;
      margin-bottom: 2px;
      display: flex;
      align-items: center;
      gap: 4px;

      &.sun { color: #dc2626; }
      &.sat { color: #2563eb; }
      &.hol { color: #dc2626; }

      .hol-name {
        font-size: 9px;
        color: #dc2626;
        background: #fee2e2;
        padding: 0 3px;
        border-radius: 2px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 60px;
      }
    }

    .day-services {
      .svc-item {
        display: flex;
        gap: 3px;
        font-size: 10px;
        padding: 1px 3px;
        margin-bottom: 1px;
        background: #e5e7eb;
        border-radius: 2px;
        overflow: hidden;

        &.family { background: #ede9fe; .svc-name { color: #6d28d9; } }
        &.severe-care { background: #fef3c7; .svc-time { color: #d97706; font-weight: 600; } .svc-name { color: #92400e; } }

        .svc-time { color: #888; flex-shrink: 0; }
        .svc-name { color: $text; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      }

      .svc-more { font-size: 10px; color: $text-sub; text-align: center; }
    }
  }
}

.patient-stats {
  padding: 12px;
  background: #fafafa;
  border-top: 1px solid $border;

  .ps-title { font-size: 12px; font-weight: 600; color: $text; margin-bottom: 8px; }

  .ps-list { display: flex; flex-wrap: wrap; gap: 6px; }

  .ps-item {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    background: #fff;
    border: 1px solid $border;

    .ps-name { font-size: 12px; font-weight: 500; color: $text; }
    .ps-count { font-size: 11px; color: $text-sub; }
    .ps-hours { font-size: 11px; color: #999; }
    .ps-severe { font-size: 10px; color: #d97706; background: #fef3c7; padding: 1px 4px; border-radius: 2px; }
  }
}

.stats-row .stat .stat-value.severe-care { color: #d97706; }
</style>
